/* global __app_id:true */ // ESLint directive to acknowledge __app_id as a global variable
/* global __firebase_config:true */ // ESLint directive to acknowledge __firebase_config as a global variable
/* global __initial_auth_token:true */ // ESLint directive to acknowledge __initial_auth_token as a global variable

import React, { useState, useEffect, useCallback } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  // signInWithCustomToken, // Not needed if anonymous sign-in is removed
  // signInAnonymously, // Not needed if anonymous sign-in is removed
} from "firebase/auth";
import {
  getFirestore,
  collection,
  addDoc,
  onSnapshot,
  query,
  serverTimestamp,
  doc,
  updateDoc,
} from "firebase/firestore";

// --- Firebase Configuration and Initialization ---
// IMPORTANT: For local development, you MUST replace the placeholder
// values below with your actual Firebase project configuration.
// You can find this in your Firebase project console:
// Project settings (gear icon) -> General -> Your apps -> Firebase SDK snippet -> Config
// If running in the Canvas environment, these will be overridden by the global variables.
const LOCAL_FIREBASE_API_KEY = "AIzaSyCgd8R5LpYv7pu9PIIl5on8YLFSVgh2A8U"; // Replace with your Firebase project's API Key
const LOCAL_FIREBASE_AUTH_DOMAIN = "ocean-logistics-tracker.firebaseapp.com"; // Replace with your Firebase project's Auth Domain
const LOCAL_FIREBASE_PROJECT_ID = "ocean-logistics-tracker"; // Replace with your Firebase project's Project ID
const LOCAL_FIREBASE_STORAGE_BUCKET =
  "ocean-logistics-tracker.firebasestorage.app"; // Replace with your Firebase project's Storage Bucket
const LOCAL_FIREBASE_MESSAGING_SENDER_ID = "986519091757"; // Replace with your Firebase project's Messaging Sender ID
const LOCAL_FIREBASE_APP_ID = "1:986519091757:web:bafd0527e8d969d48056a4"; // Replace with your Firebase project's App ID

// Determine firebaseConfig based on environment
const firebaseConfig =
  typeof __firebase_config !== "undefined" &&
  Object.keys(JSON.parse(__firebase_config)).length > 0
    ? JSON.parse(__firebase_config)
    : {
        apiKey: LOCAL_FIREBASE_API_KEY,
        authDomain: LOCAL_FIREBASE_AUTH_DOMAIN,
        projectId: LOCAL_FIREBASE_PROJECT_ID,
        storageBucket: LOCAL_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: LOCAL_FIREBASE_MESSAGING_SENDER_ID,
        appId: LOCAL_FIREBASE_APP_ID,
      };

// Use a default app ID for local development if __app_id is not defined.
// This 'default-app-id' is only for local testing and should match your Firestore rules if applied.
const appId =
  typeof __app_id !== "undefined" ? __app_id : "local-logistics-tracker-app";

// Initialize Firebase App
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);

// --- AuthScreen Component ---
function AuthScreen({ onLoginSuccess, showMessage }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState(""); // State for confirm password
  const [isLoginMode, setIsLoginMode] = useState(true); // true for Login, false for Signup
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    showMessage("", "clear"); // Clear previous messages
    setLoading(true);

    if (!isLoginMode && password !== confirmPassword) {
      showMessage("Passwords do not match.", "error");
      setLoading(false);
      return;
    }

    try {
      if (isLoginMode) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
      }
      // onLoginSuccess is implicitly handled by onAuthStateChanged in App component
    } catch (err) {
      console.error("Authentication error:", err);
      let errorMessage = "An unexpected error occurred.";
      if (err.code) {
        switch (err.code) {
          case "auth/invalid-email":
            errorMessage = "Invalid email address format.";
            break;
          case "auth/user-disabled":
            errorMessage = "This account has been disabled.";
            break;
          case "auth/user-not-found":
            errorMessage = "No user found with this email.";
            break;
          case "auth/wrong-password":
            errorMessage = "Incorrect password.";
            break;
          case "auth/email-already-in-use":
            errorMessage = "This email is already in use. Try logging in.";
            break;
          case "auth/weak-password":
            errorMessage = "Password should be at least 6 characters.";
            break;
          default:
            errorMessage = err.message;
        }
      }
      showMessage(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-100 to-indigo-200 p-4 font-inter text-gray-800">
      <div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-md">
        <h2 className="text-3xl font-extrabold text-center text-blue-700 mb-6">
          {isLoginMode ? "Login" : "Sign Up"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          {!isLoginMode && ( // Only show confirm password in signup mode
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          )}
          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75"
            disabled={loading}
          >
            {loading ? "Loading..." : isLoginMode ? "Login" : "Sign Up"}
          </button>
        </form>
        <p className="mt-6 text-center text-gray-600">
          {isLoginMode ? "Don't have an account?" : "Already have an account?"}{" "}
          <button
            type="button"
            onClick={() => {
              setIsLoginMode(!isLoginMode);
              showMessage("", "clear"); // Clear error on mode switch
              setPassword("");
              setConfirmPassword("");
            }} // Clear passwords on mode switch
            className="text-blue-600 hover:text-blue-800 font-medium focus:outline-none"
          >
            {isLoginMode ? "Sign Up" : "Login"}
          </button>
        </p>
      </div>
    </div>
  );
}

// --- App Component (Main application logic) ---
export default function App() {
  const [userId, setUserId] = useState(null);
  const [userEmail, setUserEmail] = useState(null); // State for user email
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [liveShipments, setLiveShipments] = useState([]);
  const [closedShipments, setClosedShipments] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [showShipmentForm, setShowShipmentForm] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info"); // 'info', 'success', 'error'
  const [showClosedFiles, setShowClosedFiles] = useState(false);
  const [selectedDateForMilestones, setSelectedDateForMilestones] =
    useState(null); // New state for clicked calendar date

  /**
   * Displays a message to the user.
   * @param {string} msg - The message text.
   * @param {string} type - The type of message ('info', 'success', 'error').
   */
  const showMessage = useCallback((msg, type = "info") => {
    setMessage(msg);
    setMessageType(type);
    if (msg) {
      setTimeout(() => {
        setMessage("");
        setMessageType("info");
      }, 5000); // Message disappears after 5 seconds
    }
  }, []);

  // Effect for Firebase Authentication State
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        setUserEmail(user.email);
        showMessage("Logged in successfully!", "success");
      } else {
        setUserId(null);
        setUserEmail(null);
        // --- REMOVED ANONYMOUS SIGN-IN FALLBACK ---
        // The app will now only show the AuthScreen if no user is logged in.
        // This ensures users must explicitly sign up or log in.
      }
      setIsAuthReady(true); // Auth state is known
    });

    return () => unsubscribe();
  }, [showMessage]); // Add showMessage to dependencies

  // Effect for fetching shipments from Firestore (dependent on userId and auth readiness)
  useEffect(() => {
    if (!isAuthReady || !userId) {
      setLiveShipments([]); // Clear shipments if not authenticated
      setClosedShipments([]);
      return;
    }

    const shipmentsCollectionRef = collection(
      db,
      `artifacts/${appId}/users/${userId}/shipments`
    );
    const q = query(shipmentsCollectionRef);

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const fetchedShipments = snapshot.docs.map((doc) => {
          const data = doc.data();
          const processDate = (dateField) => {
            if (data[dateField] && data[dateField].date) {
              // Firestore Timestamps need to be converted to Date objects
              return {
                date: data[dateField].date.toDate(),
                isCompleted: data[dateField].isCompleted || false,
              };
            }
            return { date: null, isCompleted: false };
          };

          const customMilestones = (data.customMilestones || []).map((cm) => ({
            ...cm,
            date: cm.date ? cm.date.toDate() : null, // Convert Timestamp to Date
          }));

          return {
            id: doc.id,
            ...data,
            pickupDate: processDate("pickupDate"),
            containerCutoffDate: processDate("containerCutoffDate"),
            vgmCutoffDate: processDate("vgmCutoffDate"),
            shippingInstructionCutoffDate: processDate(
              "shippingInstructionCutoffDate"
            ),
            etdOrigin: processDate("etdOrigin"),
            etaDestination: processDate("etaDestination"),
            etdFinalDestination: processDate("etdFinalDestination"),
            customMilestones: customMilestones,
            isClosed: data.isClosed || false,
          };
        });

        setLiveShipments(fetchedShipments.filter((s) => !s.isClosed));
        setClosedShipments(fetchedShipments.filter((s) => s.isClosed));
      },
      (error) => {
        console.error("Error fetching shipments:", error);
        showMessage(
          "Failed to load shipments. Please check your connection.",
          "error"
        );
      }
    );

    return () => unsubscribe();
  }, [db, userId, isAuthReady, showMessage]); // Re-run when db, userId, or auth state changes

  // Function to add a new shipment to Firestore
  const addShipment = async (shipmentData) => {
    if (!userId) {
      showMessage("User not authenticated. Cannot add shipment.", "error");
      return;
    }
    try {
      const shipmentsCollectionRef = collection(
        db,
        `artifacts/${appId}/users/${userId}/shipments`
      );
      await addDoc(shipmentsCollectionRef, {
        ...shipmentData,
        createdAt: serverTimestamp(), // Use serverTimestamp for accurate creation time
        isClosed: false,
      });
      showMessage("Shipment added successfully!", "success");
      setShowShipmentForm(false);
    } catch (e) {
      console.error("Error adding document: ", e);
      showMessage("Failed to add shipment. Please try again.", "error");
    }
  };

  // Function to update an existing shipment in Firestore
  const updateShipment = async (shipmentId, updatedData) => {
    if (!userId) {
      showMessage("User not authenticated. Cannot update shipment.", "error");
      return;
    }
    try {
      const shipmentDocRef = doc(
        db,
        `artifacts/${appId}/users/${userId}/shipments`,
        shipmentId
      );
      const dataToUpdate = {};
      for (const key in updatedData) {
        if (
          updatedData[key] &&
          typeof updatedData[key] === "object" &&
          updatedData[key].date instanceof Date
        ) {
          // Convert Date objects back to Firestore Timestamps if necessary, or just pass Date objects
          // Firestore handles Date objects automatically, no explicit Timestamp conversion needed here.
          dataToUpdate[key] = {
            date: updatedData[key].date,
            isCompleted: updatedData[key].isCompleted,
          };
        } else if (
          key === "customMilestones" &&
          Array.isArray(updatedData[key])
        ) {
          dataToUpdate[key] = updatedData[key].map((cm) => ({
            ...cm,
            date:
              cm.date instanceof Date
                ? cm.date
                : cm.date
                ? new Date(cm.date)
                : null, // Ensure dates are Date objects
          }));
        } else {
          dataToUpdate[key] = updatedData[key];
        }
      }

      await updateDoc(shipmentDocRef, dataToUpdate);
      showMessage("Shipment updated successfully!", "success");
      setSelectedShipment(null);
    } catch (e) {
      console.error("Error updating document: ", e);
      showMessage("Failed to update shipment. Please try again.", "error");
    }
  };

  // --- Calendar Logic ---
  const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  // Helper to get all milestones for a shipment, regardless of completion or future/past
  const getAllMilestonesForShipment = (shipment) => {
    const fixedMilestones = [
      {
        type: "Pickup",
        data: shipment.pickupDate,
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      },
      {
        type: "Container Cut-off",
        data: shipment.containerCutoffDate,
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      },
      {
        type: "VGM Cut-off",
        data: shipment.vgmCutoffDate,
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      },
      {
        type: "SI Cut-off",
        data: shipment.shippingInstructionCutoffDate,
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      },
      {
        type: "ETD Origin",
        data: shipment.etdOrigin,
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      },
      {
        type: "ETA Destination",
        data: shipment.etaDestination,
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      },
      {
        type: "ETA Final Dest.",
        data: shipment.etdFinalDestination,
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      },
    ].filter((m) => m.data && m.data.date); // Filter out milestones without a date

    const customMilestones = (shipment.customMilestones || [])
      .map((cm) => ({
        type: cm.name,
        data: { date: cm.date, isCompleted: cm.isCompleted },
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      }))
      .filter((m) => m.data && m.data.date); // Filter out custom milestones without a date

    return [...fixedMilestones, ...customMilestones];
  };

  const getNextUncompletedMilestone = (shipment) => {
    const allMilestones = getAllMilestonesForShipment(shipment);

    allMilestones.sort((a, b) => a.data.date.getTime() - b.data.date.getTime());

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    for (const milestone of allMilestones) {
      const milestoneDate = new Date(milestone.data.date);
      milestoneDate.setHours(0, 0, 0, 0);

      if (!milestone.data.isCompleted && milestoneDate >= now) {
        return milestone;
      }
    }
    // If all future milestones are completed, return the first uncompleted past milestone
    for (const milestone of allMilestones) {
      if (!milestone.data.isCompleted) {
        return milestone;
      }
    }
    return null; // All milestones completed
  };

  const generateCalendarDays = useCallback(() => {
    const numDays = daysInMonth(currentMonth, currentYear);
    const startDay = firstDayOfMonth(currentMonth, currentYear);
    const days = [];

    // Add empty days for the beginning of the month
    for (let i = 0; i < startDay; i++) {
      days.push({ type: "empty", date: null, events: [] });
    }

    // Add days of the month with events
    for (let i = 1; i <= numDays; i++) {
      const date = new Date(currentYear, currentMonth, i);
      const dayEvents = [];

      liveShipments.forEach((shipment) => {
        const allShipmentMilestones = getAllMilestonesForShipment(shipment);
        const nextUncompleted = getNextUncompletedMilestone(shipment); // Still needed for highlighting

        allShipmentMilestones.forEach((milestone) => {
          if (
            milestone.data.date.getDate() === i &&
            milestone.data.date.getMonth() === currentMonth &&
            milestone.data.date.getFullYear() === currentYear
          ) {
            dayEvents.push({
              type: milestone.type,
              shipmentReference: milestone.shipmentReference,
              shipmentId: milestone.shipmentId,
              date: milestone.data.date,
              isCompleted: milestone.data.isCompleted,
              isNextUncompleted:
                nextUncompleted &&
                nextUncompleted.shipmentId === milestone.shipmentId &&
                nextUncompleted.type === milestone.type, // Check if this is the next uncompleted
              fullShipment: milestone.fullShipment,
            });
          }
        });
      });
      // Sort events within a day by completion status (uncompleted first) and then by time
      dayEvents.sort((a, b) => {
        if (a.isCompleted === b.isCompleted) {
          return a.date.getTime() - b.date.getTime();
        }
        return a.isCompleted ? 1 : -1; // Uncompleted (false) comes before completed (true)
      });

      days.push({ type: "day", date, events: dayEvents });
    }
    return days;
  }, [currentMonth, currentYear, liveShipments]);

  const handleMonthChange = (direction) => {
    let newMonth = currentMonth + direction;
    let newYear = currentYear;

    if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    } else if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    }
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getEventTypeColor = (type) => {
    switch (type) {
      case "Pickup":
        return "bg-blue-500";
      case "Container Cut-off":
        return "bg-red-500";
      case "VGM Cut-off":
        return "bg-purple-500";
      case "SI Cut-off":
        return "bg-green-500";
      case "ETD Origin":
        return "bg-yellow-500 text-gray-800";
      case "ETA Destination":
        return "bg-teal-500";
      case "ETA Final Dest.":
        return "bg-indigo-500";
      default:
        return "bg-gray-500";
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      showMessage("Logged out successfully!", "success");
    } catch (error) {
      console.error("Error logging out:", error);
      showMessage("Failed to log out. Please try again.", "error");
    }
  };

  // Render loading state while authentication is being checked
  if (!isAuthReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-100 to-indigo-200 text-gray-800">
        <p className="text-xl font-semibold">Loading authentication...</p>
      </div>
    );
  }

  // Render authentication screen if no user is logged in
  if (!userId) {
    return <AuthScreen showMessage={showMessage} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 to-indigo-200 p-4 font-inter text-gray-800">
      {/* Message Display */}
      {message && (
        <div
          className={`fixed top-4 right-4 px-4 py-3 rounded-lg shadow-lg z-50 animate-fade-in-down
            ${messageType === "success" ? "bg-green-600 text-white" : ""}
            ${messageType === "error" ? "bg-red-600 text-white" : ""}
            ${messageType === "info" ? "bg-blue-600 text-white" : ""}
          `}
        >
          {message}
          <button
            onClick={() => showMessage("", "clear")} // Clear message
            className="ml-4 text-white hover:text-gray-200 font-bold"
          >
            &times;
          </button>
        </div>
      )}

      <div className="max-w-6xl mx-auto bg-white rounded-xl shadow-xl p-6 md:p-8">
        <h1 className="text-4xl font-extrabold text-center text-blue-700 mb-6 tracking-tight">
          Calean
        </h1>

        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
          <div className="text-sm text-gray-500">
            Logged in as:{" "}
            <span className="font-mono bg-gray-100 px-2 py-1 rounded-md text-gray-700">
              {userEmail || userId}
            </span>
          </div>
          <button
            onClick={handleLogout}
            className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-75"
          >
            Logout
          </button>
        </div>

        <div className="flex justify-center mb-6">
          <button
            onClick={() => {
              setShowShipmentForm(!showShipmentForm);
              setSelectedShipment(null); // Deselect any shipment when opening/closing form
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75"
          >
            {showShipmentForm ? "Hide Shipment Form" : "Add New Shipment File"}
          </button>
        </div>

        {showShipmentForm && (
          <ShipmentForm
            addShipment={addShipment}
            setShowShipmentForm={setShowShipmentForm}
            showMessage={showMessage} // Pass showMessage to ShipmentForm
          />
        )}

        <div className="flex justify-between items-center bg-blue-500 text-white p-4 rounded-t-lg shadow-md mb-4">
          <button
            onClick={() => handleMonthChange(-1)}
            className="p-2 rounded-full hover:bg-blue-600 transition duration-200 focus:outline-none focus:ring-2 focus:ring-white"
            aria-label="Previous Month"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h2 className="text-2xl font-bold">
            {monthNames[currentMonth]} {currentYear}
          </h2>
          <button
            onClick={() => handleMonthChange(1)}
            className="p-2 rounded-full hover:bg-blue-600 transition duration-200 focus:outline-none focus:ring-2 focus:ring-white"
            aria-label="Next Month"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 text-center">
          {dayNames.map((day) => (
            <div key={day} className="font-semibold text-gray-600 py-2">
              {day}
            </div>
          ))}
          {generateCalendarDays().map((day, index) => (
            <div
              key={index}
              className={`
                p-2 rounded-lg min-h-[100px] flex flex-col items-center justify-start relative
                ${
                  day.type === "empty"
                    ? "bg-gray-100"
                    : "bg-gray-50 hover:bg-gray-100 cursor-pointer"
                }
                ${
                  day.date &&
                  day.date.toDateString() === new Date().toDateString()
                    ? "border-2 border-blue-500 bg-blue-50 text-blue-800 font-semibold"
                    : ""
                }
                shadow-sm transition duration-150 ease-in-out
              `}
              onClick={() =>
                day.type === "day" && setSelectedDateForMilestones(day.date)
              } // Set selected date on click
            >
              {day.type === "day" && (
                <>
                  <span className="font-bold text-lg mb-1">
                    {day.date.getDate()}
                  </span>
                  {day.events.length > 0 && (
                    <div className="w-full mt-1 space-y-1">
                      {day.events.map((event, eventIndex) => (
                        <div // Changed from button to div, as the modal handles click
                          key={eventIndex}
                          className={`
                            text-xs p-1 rounded-md text-white truncate w-full text-left
                            ${getEventTypeColor(event.type)}
                            ${
                              event.isCompleted ? "opacity-60 line-through" : ""
                            }
                            ${
                              event.isNextUncompleted
                                ? "ring-2 ring-offset-2 ring-yellow-400 font-bold"
                                : ""
                            }
                          `}
                          title={`${event.type}: ${event.shipmentReference} ${
                            event.isCompleted ? "(Completed)" : ""
                          }`}
                        >
                          {event.type.split(" ")[0]} - {event.shipmentReference}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8">
          <h2 className="text-3xl font-bold text-blue-700 mb-4 text-center">
            Live Shipments
          </h2>
          {liveShipments.length === 0 ? (
            <p className="text-center text-gray-600">
              No live shipments found. Add one to get started!
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {liveShipments.map((shipment) => (
                <div
                  key={shipment.id}
                  className="bg-white border border-gray-200 rounded-lg shadow-md p-4 flex flex-col justify-between"
                >
                  <div>
                    <h3 className="text-lg font-semibold text-blue-600 mb-2">
                      {shipment.shipmentReference}
                    </h3>
                    <p className="text-sm text-gray-700">
                      <strong>Booking #:</strong>{" "}
                      {shipment.bookingNumber || "N/A"}
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>Line:</strong> {shipment.steamshipLine}
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>Origin:</strong> {shipment.portOfOrigin}
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>Destination:</strong> {shipment.portOfDestination}
                    </p>
                    {shipment.notes && (
                      <p
                        className="text-sm text-gray-600 mt-2 italic truncate"
                        title={shipment.notes}
                      >
                        Notes: {shipment.notes}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setSelectedShipment(shipment);
                      setShowShipmentForm(false);
                    }}
                    className="mt-4 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-75"
                  >
                    View/Manage Details
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-8 border-t border-gray-200 pt-8">
          <h2
            className="text-3xl font-bold text-blue-700 mb-4 text-center cursor-pointer flex items-center justify-center"
            onClick={() => setShowClosedFiles(!showClosedFiles)}
          >
            Closed Shipments
            <svg
              className={`ml-2 h-6 w-6 transform transition-transform duration-300 ${
                showClosedFiles ? "rotate-180" : ""
              }`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </h2>
          {showClosedFiles &&
            (closedShipments.length === 0 ? (
              <p className="text-center text-gray-600">
                No closed shipments found.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {closedShipments.map((shipment) => (
                  <div
                    key={shipment.id}
                    className="bg-gray-100 border border-gray-300 rounded-lg shadow-sm p-4 flex flex-col justify-between opacity-80"
                  >
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">
                        {shipment.shipmentReference}
                      </h3>
                      <p className="text-sm text-gray-600">
                        <strong>Booking #:</strong>{" "}
                        {shipment.bookingNumber || "N/A"}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Line:</strong> {shipment.steamshipLine}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Origin:</strong> {shipment.portOfOrigin}
                      </p>
                      <p className="text-sm text-gray-600">
                        <strong>Destination:</strong>{" "}
                        {shipment.portOfDestination}
                      </p>
                      {shipment.notes && (
                        <p
                          className="text-sm text-gray-500 mt-2 italic truncate"
                          title={shipment.notes}
                        >
                          Notes: {shipment.notes}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setSelectedShipment(shipment);
                        setShowShipmentForm(false);
                      }}
                      className="mt-4 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-75"
                    >
                      View Details
                    </button>
                  </div>
                ))}
              </div>
            ))}
        </div>

        {selectedShipment && (
          <ShipmentDetailsModal
            shipment={selectedShipment}
            updateShipment={updateShipment}
            onClose={() => setSelectedShipment(null)}
            showMessage={showMessage} // Pass showMessage to ShipmentDetailsModal
          />
        )}

        {selectedDateForMilestones && (
          <MilestonesForDayModal
            selectedDate={selectedDateForMilestones}
            liveShipments={liveShipments}
            onClose={() => setSelectedDateForMilestones(null)}
            onViewShipmentDetails={setSelectedShipment} // Pass function to open shipment details
            getEventTypeColor={getEventTypeColor} // Pass color helper
          />
        )}
      </div>
    </div>
  );
}

// --- ShipmentForm Component (for adding new shipments) ---
function ShipmentForm({ addShipment, setShowShipmentForm, showMessage }) {
  const [shipmentReference, setShipmentReference] = useState("");
  const [bookingNumber, setBookingNumber] = useState("");
  const [steamshipLine, setSteamshipLine] = useState("");
  const [placeOfReceiving, setPlaceOfReceiving] = useState("");
  const [portOfOrigin, setPortOfOrigin] = useState("");
  const [portOfDestination, setPortOfDestination] = useState("");
  const [finalDestination, setFinalDestination] = useState("");
  const [etdOrigin, setEtdOrigin] = useState("");
  const [etaDestination, setEtaDestination] = useState("");
  const [etdFinalDestination, setEtdFinalDestination] = useState("");
  const [pickupDate, setPickupDate] = useState("");
  const [containerCutoffDate, setContainerCutoffDate] = useState("");
  const [vgmCutoffDate, setVgmCutoffDate] = useState("");
  const [shippingInstructionCutoffDate, setShippingInstructionCutoffDate] =
    useState("");
  const [notes, setNotes] = useState("");
  const [customMilestones, setCustomMilestones] = useState([]);

  const handleAddCustomMilestone = () => {
    setCustomMilestones([
      ...customMilestones,
      { name: "", date: "", isCompleted: false },
    ]);
  };

  const handleCustomMilestoneChange = (index, field, value) => {
    const newCustomMilestones = [...customMilestones];
    newCustomMilestones[index][field] = value;
    setCustomMilestones(newCustomMilestones);
  };

  const handleRemoveCustomMilestone = (index) => {
    setCustomMilestones(customMilestones.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      !shipmentReference ||
      !steamshipLine ||
      !portOfOrigin ||
      !portOfDestination
    ) {
      showMessage(
        "Shipment Reference, Steamship Line, Port of Origin, and Port of Destination are required!",
        "error"
      );
      return;
    }

    const processedCustomMilestones = customMilestones
      .map((cm) => ({
        name: cm.name,
        date: cm.date ? new Date(cm.date + "T00:00:00") : null,
        isCompleted: cm.isCompleted,
      }))
      .filter((cm) => cm.name && cm.date); // Only include custom milestones with both name and date

    const newShipment = {
      shipmentReference,
      bookingNumber,
      steamshipLine,
      placeOfReceiving,
      portOfOrigin,
      portOfDestination,
      finalDestination,
      etdOrigin: etdOrigin
        ? { date: new Date(etdOrigin + "T00:00:00"), isCompleted: false }
        : { date: null, isCompleted: false },
      etaDestination: etaDestination
        ? { date: new Date(etaDestination + "T00:00:00"), isCompleted: false }
        : { date: null, isCompleted: false },
      etdFinalDestination: etdFinalDestination
        ? {
            date: new Date(etdFinalDestination + "T00:00:00"),
            isCompleted: false,
          }
        : { date: null, isCompleted: false },
      pickupDate: pickupDate
        ? { date: new Date(pickupDate + "T00:00:00"), isCompleted: false }
        : { date: null, isCompleted: false },
      containerCutoffDate: containerCutoffDate
        ? {
            date: new Date(containerCutoffDate + "T00:00:00"),
            isCompleted: false,
          }
        : { date: null, isCompleted: false },
      vgmCutoffDate: vgmCutoffDate
        ? { date: new Date(vgmCutoffDate + "T00:00:00"), isCompleted: false }
        : { date: null, isCompleted: false },
      shippingInstructionCutoffDate: shippingInstructionCutoffDate
        ? {
            date: new Date(shippingInstructionCutoffDate + "T00:00:00"),
            isCompleted: false,
          }
        : { date: null, isCompleted: false },
      notes,
      customMilestones: processedCustomMilestones,
      isClosed: false,
    };
    addShipment(newShipment);
    // Clear form fields after submission
    setShipmentReference("");
    setBookingNumber("");
    setSteamshipLine("");
    setPlaceOfReceiving("");
    setPortOfOrigin("");
    setPortOfDestination("");
    setFinalDestination("");
    setEtdOrigin("");
    setEtaDestination("");
    setEtdFinalDestination("");
    setPickupDate("");
    setContainerCutoffDate("");
    setVgmCutoffDate("");
    setShippingInstructionCutoffDate("");
    setNotes("");
    setCustomMilestones([]);
  };

  return (
    <div className="bg-blue-50 p-6 rounded-lg shadow-inner mb-8 transition-all duration-300 ease-in-out">
      <h3 className="text-2xl font-bold text-blue-700 mb-4 text-center">
        Add New Shipment File
      </h3>
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <div>
          <label
            htmlFor="shipmentReference"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Shipment Reference <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="shipmentReference"
            value={shipmentReference}
            onChange={(e) => setShipmentReference(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
        <div>
          <label
            htmlFor="bookingNumber"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Booking #
          </label>
          <input
            type="text"
            id="bookingNumber"
            value={bookingNumber}
            onChange={(e) => setBookingNumber(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label
            htmlFor="steamshipLine"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Steamship Line <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="steamshipLine"
            value={steamshipLine}
            onChange={(e) => setSteamshipLine(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
        <div>
          <label
            htmlFor="placeOfReceiving"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Place of Receiving
          </label>
          <input
            type="text"
            id="placeOfReceiving"
            value={placeOfReceiving}
            onChange={(e) => setPlaceOfReceiving(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label
            htmlFor="portOfOrigin"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Port of Origin <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="portOfOrigin"
            value={portOfOrigin}
            onChange={(e) => setPortOfOrigin(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
        <div>
          <label
            htmlFor="portOfDestination"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Port of Destination <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="portOfDestination"
            value={portOfDestination}
            onChange={(e) => setPortOfDestination(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
        <div>
          <label
            htmlFor="finalDestination"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Final Destination
          </label>
          <input
            type="text"
            id="finalDestination"
            value={finalDestination}
            onChange={(e) => setFinalDestination(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label
            htmlFor="etdOrigin"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            ETD Origin
          </label>
          <input
            type="date"
            id="etdOrigin"
            value={etdOrigin}
            onChange={(e) => setEtdOrigin(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label
            htmlFor="etaDestination"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            ETA Destination
          </label>
          <input
            type="date"
            id="etaDestination"
            value={etaDestination}
            onChange={(e) => setEtaDestination(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="col-span-1 md:col-span-2">
          <label
            htmlFor="etdFinalDestination"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            ETA Final Destination
          </label>
          <input
            type="date"
            id="etdFinalDestination"
            value={etdFinalDestination}
            onChange={(e) => setEtdFinalDestination(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div>
          <label
            htmlFor="pickupDate"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Pick-up Date
          </label>
          <input
            type="date"
            id="pickupDate"
            value={pickupDate}
            onChange={(e) => setPickupDate(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label
            htmlFor="containerCutoffDate"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Container Cut-off Date
          </label>
          <input
            type="date"
            id="containerCutoffDate"
            value={containerCutoffDate}
            onChange={(e) => setContainerCutoffDate(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label
            htmlFor="vgmCutoffDate"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            VGM Cut-off Date
          </label>
          <input
            type="date"
            id="vgmCutoffDate"
            value={vgmCutoffDate}
            onChange={(e) => setVgmCutoffDate(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label
            htmlFor="shippingInstructionCutoffDate"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Shipping Instruction Cut-off Date
          </label>
          <input
            type="date"
            id="shippingInstructionCutoffDate"
            value={shippingInstructionCutoffDate}
            onChange={(e) => setShippingInstructionCutoffDate(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="col-span-1 md:col-span-2 border-t border-gray-200 pt-4 mt-4">
          <h4 className="text-xl font-bold text-blue-600 mb-3">
            Custom Milestones
          </h4>
          {customMilestones.map((cm, index) => (
            <div
              key={index}
              className="flex flex-col sm:flex-row items-center gap-2 mb-3 p-3 bg-gray-100 rounded-md"
            >
              <input
                type="text"
                placeholder="Milestone Name"
                value={cm.name}
                onChange={(e) =>
                  handleCustomMilestoneChange(index, "name", e.target.value)
                }
                className="w-full sm:w-1/2 p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <input
                type="date"
                value={cm.date}
                onChange={(e) =>
                  handleCustomMilestoneChange(index, "date", e.target.value)
                }
                className="w-full sm:w-1/2 p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={() => handleRemoveCustomMilestone(index)}
                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-md transition duration-200"
                title="Remove Custom Milestone"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm6 0a1 1 0 01-2 0v6a1 1 0 112 0V8z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddCustomMilestone}
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75"
          >
            Add Custom Milestone
          </button>
        </div>

        <div className="col-span-1 md:col-span-2">
          <label
            htmlFor="notes"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Notes for Operator
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows="4"
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
          ></textarea>
        </div>

        <div className="col-span-1 md:col-span-2 flex justify-end space-x-4 mt-4">
          <button
            type="button"
            onClick={() => setShowShipmentForm(false)}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-75"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-75"
          >
            Save Shipment
          </button>
        </div>
      </form>
    </div>
  );
}

// --- ShipmentDetailsModal Component ---
function ShipmentDetailsModal({
  shipment,
  updateShipment,
  onClose,
  showMessage,
}) {
  const [editableShipment, setEditableShipment] = useState(shipment);

  // Helper function to format Date objects for HTML date input
  const formatDateForInput = (dateObj) => {
    if (!dateObj) return "";
    const date = new Date(dateObj);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Handles toggling the completion status of a predefined milestone
  const handleMilestoneToggle = (milestoneKey) => {
    setEditableShipment((prev) => ({
      ...prev,
      [milestoneKey]: {
        ...prev[milestoneKey],
        isCompleted: !prev[milestoneKey].isCompleted,
      },
    }));
  };

  // Handles changes to static shipment information (text inputs)
  const handleStaticInfoChange = (e) => {
    const { id, value } = e.target;
    setEditableShipment((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  // Handles changes to date inputs for predefined milestones
  const handleDateChange = (e, milestoneKey) => {
    const dateString = e.target.value;
    setEditableShipment((prev) => ({
      ...prev,
      [milestoneKey]: {
        ...prev[milestoneKey],
        date: dateString ? new Date(dateString + "T00:00:00") : null, // Ensure date is stored as Date object
      },
    }));
  };

  // Adds a new empty custom milestone row
  const handleAddCustomMilestone = () => {
    setEditableShipment((prev) => ({
      ...prev,
      customMilestones: [
        ...(prev.customMilestones || []),
        { name: "", date: "", isCompleted: false },
      ],
    }));
  };

  // Handles changes to custom milestone fields (name, date, completed status)
  const handleCustomMilestoneChange = (index, field, value) => {
    setEditableShipment((prev) => {
      const newCustomMilestones = [...(prev.customMilestones || [])];
      if (field === "date") {
        newCustomMilestones[index][field] = value
          ? new Date(value + "T00:00:00")
          : null;
      } else if (field === "isCompleted") {
        newCustomMilestones[index][field] = value;
      } else {
        newCustomMilestones[index][field] = value;
      }
      return { ...prev, customMilestones: newCustomMilestones };
    });
  };

  // Removes a custom milestone row
  const handleRemoveCustomMilestone = (index) => {
    setEditableShipment((prev) => ({
      ...prev,
      customMilestones: (prev.customMilestones || []).filter(
        (_, i) => i !== index
      ),
    }));
  };

  // Toggles the 'isClosed' status of the shipment
  const handleToggleClosed = () => {
    setEditableShipment((prev) => ({
      ...prev,
      isClosed: !prev.isClosed,
    }));
  };

  // Saves the changes to the shipment
  const handleSave = () => {
    // Basic validation for required fields before saving
    if (
      !editableShipment.shipmentReference ||
      !editableShipment.steamshipLine ||
      !editableShipment.portOfOrigin ||
      !editableShipment.portOfDestination
    ) {
      showMessage(
        "Shipment Reference, Steamship Line, Port of Origin, and Port of Destination are required!",
        "error"
      );
      return;
    }
    updateShipment(editableShipment.id, editableShipment);
  };

  // Array defining the predefined milestone fields for easier rendering
  const milestoneFields = [
    { key: "pickupDate", label: "Pick-up Date" },
    { key: "containerCutoffDate", label: "Container Cut-off Date" },
    { key: "vgmCutoffDate", label: "VGM Cut-off Date" },
    {
      key: "shippingInstructionCutoffDate",
      label: "Shipping Instruction Cut-off Date",
    },
    { key: "etdOrigin", label: "ETD Origin" },
    { key: "etaDestination", label: "ETA Destination" },
    { key: "etaFinalDestination", label: "ETA Final Destination" },
  ];

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl p-6 md:p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-3xl font-bold focus:outline-none"
          aria-label="Close"
        >
          &times;
        </button>
        <h3 className="text-3xl font-bold text-blue-700 mb-6 text-center">
          Shipment Details: {editableShipment.shipmentReference}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label
              htmlFor="shipmentReference"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Shipment Reference:
            </label>
            <input
              type="text"
              id="shipmentReference"
              value={editableShipment.shipmentReference}
              onChange={handleStaticInfoChange}
              className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label
              htmlFor="bookingNumber"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Booking #:
            </label>
            <input
              type="text"
              id="bookingNumber"
              value={editableShipment.bookingNumber || ""}
              onChange={handleStaticInfoChange}
              className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label
              htmlFor="steamshipLine"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Steamship Line:
            </label>
            <input
              type="text"
              id="steamshipLine"
              value={editableShipment.steamshipLine}
              onChange={handleStaticInfoChange}
              className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label
              htmlFor="placeOfReceiving"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Place of Receiving:
            </label>
            <input
              type="text"
              id="placeOfReceiving"
              value={editableShipment.placeOfReceiving || ""}
              onChange={handleStaticInfoChange}
              className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label
              htmlFor="portOfOrigin"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Port of Origin:
            </label>
            <input
              type="text"
              id="portOfOrigin"
              value={editableShipment.portOfOrigin}
              onChange={handleStaticInfoChange}
              className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label
              htmlFor="portOfDestination"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Port of Destination:
            </label>
            <input
              type="text"
              id="portOfDestination"
              value={editableShipment.portOfDestination}
              onChange={handleStaticInfoChange}
              className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label
              htmlFor="finalDestination"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Final Destination:
            </label>
            <input
              type="text"
              id="finalDestination"
              value={editableShipment.finalDestination || ""}
              onChange={handleStaticInfoChange}
              className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="mb-6 border-t border-gray-200 pt-6">
          <h4 className="text-xl font-bold text-blue-600 mb-4">
            Predefined Milestones
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {milestoneFields.map((field) => {
              const milestone = editableShipment[field.key];
              const dateValue =
                milestone && milestone.date
                  ? formatDateForInput(milestone.date)
                  : "";
              const isCompleted = milestone && milestone.isCompleted;

              return (
                <div
                  key={field.key}
                  className="flex flex-col bg-gray-50 p-3 rounded-md shadow-sm"
                >
                  <label
                    htmlFor={field.key}
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {field.label}:
                  </label>
                  <div className="flex items-center justify-between">
                    <input
                      type="date"
                      id={field.key}
                      value={dateValue}
                      onChange={(e) => handleDateChange(e, field.key)}
                      className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 mr-2"
                    />
                    {dateValue && ( // Only show checkbox if a date is set
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isCompleted}
                          onChange={() => handleMilestoneToggle(field.key)}
                          className="form-checkbox h-5 w-5 text-green-600 rounded focus:ring-green-500 transition duration-150 ease-in-out"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {isCompleted ? "Completed" : "Mark Complete"}
                        </span>
                      </label>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="col-span-1 md:col-span-2 border-t border-gray-200 pt-4 mt-4">
          <h4 className="text-xl font-bold text-blue-600 mb-3">
            Custom Milestones
          </h4>
          {(editableShipment.customMilestones || []).map((cm, index) => (
            <div
              key={index}
              className="flex flex-col sm:flex-row items-center gap-2 mb-3 p-3 bg-gray-100 rounded-md"
            >
              <input
                type="text"
                placeholder="Milestone Name"
                value={cm.name}
                onChange={(e) =>
                  handleCustomMilestoneChange(index, "name", e.target.value)
                }
                className="w-full sm:w-1/3 p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <input
                type="date"
                value={cm.date ? formatDateForInput(cm.date) : ""}
                onChange={(e) =>
                  handleCustomMilestoneChange(index, "date", e.target.value)
                }
                className="w-full sm:w-1/3 p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
              <label className="flex items-center cursor-pointer sm:w-1/6">
                <input
                  type="checkbox"
                  checked={cm.isCompleted}
                  onChange={(e) =>
                    handleCustomMilestoneChange(
                      index,
                      "isCompleted",
                      e.target.checked
                    )
                  }
                  className="form-checkbox h-5 w-5 text-green-600 rounded focus:ring-green-500 transition duration-150 ease-in-out"
                />
                <span className="ml-2 text-sm text-gray-700">Completed</span>
              </label>
              <button
                type="button"
                onClick={() => handleRemoveCustomMilestone(index)}
                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-md transition duration-200"
                title="Remove Custom Milestone"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm6 0a1 1 0 01-2 0v6a1 1 0 112 0V8z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddCustomMilestone}
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75"
          >
            Add Custom Milestone
          </button>
        </div>

        <div className="mb-6 border-t border-gray-200 pt-6">
          <h4 className="text-xl font-bold text-blue-600 mb-4">
            Notes for Operator
          </h4>
          <textarea
            value={editableShipment.notes || ""}
            onChange={handleStaticInfoChange}
            id="notes"
            rows="5"
            className="w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="Add notes related to this shipment..."
          ></textarea>
        </div>

        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4 mt-6">
          <button
            type="button"
            onClick={handleToggleClosed}
            className={`font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-opacity-75
              ${
                editableShipment.isClosed
                  ? "bg-green-600 hover:bg-green-700 text-white focus:ring-green-500"
                  : "bg-red-600 hover:bg-red-700 text-white focus:ring-red-500"
              }`}
          >
            {editableShipment.isClosed ? "Reopen File" : "Mark as Closed"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-75"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-75"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// --- MilestonesForDayModal Component (NEW) ---
function MilestonesForDayModal({
  selectedDate,
  liveShipments,
  onClose,
  onViewShipmentDetails,
  getEventTypeColor,
}) {
  // Filter all milestones from all live shipments for the selected date
  const milestonesForDay = liveShipments.flatMap((shipment) => {
    const allShipmentMilestones = [
      {
        type: "Pickup",
        data: shipment.pickupDate,
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      },
      {
        type: "Container Cut-off",
        data: shipment.containerCutoffDate,
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      },
      {
        type: "VGM Cut-off",
        data: shipment.vgmCutoffDate,
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      },
      {
        type: "SI Cut-off",
        data: shipment.shippingInstructionCutoffDate,
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      },
      {
        type: "ETD Origin",
        data: shipment.etdOrigin,
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      },
      {
        type: "ETA Destination",
        data: shipment.etaDestination,
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      },
      {
        type: "ETA Final Dest.",
        data: shipment.etdFinalDestination,
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      },
      ...(shipment.customMilestones || []).map((cm) => ({
        type: cm.name,
        data: { date: cm.date, isCompleted: cm.isCompleted },
        shipmentId: shipment.id,
        shipmentReference: shipment.shipmentReference,
        fullShipment: shipment,
      })),
    ].filter(
      (m) =>
        m.data &&
        m.data.date &&
        m.data.date.getDate() === selectedDate.getDate() &&
        m.data.date.getMonth() === selectedDate.getMonth() &&
        m.data.date.getFullYear() === selectedDate.getFullYear()
    );
    return allShipmentMilestones;
  });

  // Sort milestones: uncompleted first, then by type/name
  milestonesForDay.sort((a, b) => {
    if (a.data.isCompleted === b.data.isCompleted) {
      return a.type.localeCompare(b.type);
    }
    return a.data.isCompleted ? 1 : -1; // Uncompleted (false) comes before completed (true)
  });

  const formattedDate = selectedDate.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl p-6 md:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-3xl font-bold focus:outline-none"
          aria-label="Close"
        >
          &times;
        </button>
        <h3 className="text-2xl font-bold text-blue-700 mb-4 text-center">
          Milestones for {formattedDate}
        </h3>

        {milestonesForDay.length === 0 ? (
          <p className="text-center text-gray-600">
            No milestones for this day.
          </p>
        ) : (
          <div className="space-y-3">
            {milestonesForDay.map((milestone, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg flex justify-between items-center
                  ${getEventTypeColor(milestone.type)}
                  ${milestone.data.isCompleted ? "opacity-60 line-through" : ""}
                `}
              >
                <div className="flex-1 text-white">
                  <p className="font-semibold">{milestone.type}</p>
                  <p className="text-sm">
                    Shipment: {milestone.shipmentReference}
                  </p>
                </div>
                <button
                  onClick={() => {
                    onViewShipmentDetails(milestone.fullShipment);
                    onClose(); // Close this modal when opening shipment details
                  }}
                  className="ml-4 bg-white text-blue-600 hover:bg-blue-100 font-semibold py-1 px-3 rounded-md shadow-sm transition duration-200"
                >
                  View Shipment
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-center mt-6">
          <button
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-75"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
